/**
 * Domain Entity: Chat
 * Represents a chat message with business logic
 */
export class Chat {
  private constructor(
    private readonly _id: string,
    private readonly _userId: string,
    private readonly _message: string,
    private readonly _type: "user" | "ai",
    private readonly _timestamp: Date,
    private readonly _isRead: boolean = false,
    private readonly _metadata?: Record<string, unknown>,
    private readonly _parsedAI?: unknown,
    private readonly _filePath?: string
  ) {}

  static create(
    id: string,
    userId: string,
    message: string,
    type: "user" | "ai",
    metadata?: Record<string, unknown>,
    parsedAI?: unknown,
    filePath?: string 
  ): Chat {
    if (!id || !userId) {
      throw new Error("Chat ID and User ID are required");
    }

    if (!message && !filePath) {
      throw new Error("Either message or image is required");
    }

    if (message && message.length > 1000) {
      throw new Error("Message cannot exceed 1000 characters");
    }

    return new Chat(id, userId, message, type, new Date(), false, metadata, parsedAI, filePath);
  }

  static fromData(data: {
    id: string;
    user_id: string;
    message: string;
    type: "user" | "ai";
    timestamp: string;
    is_read?: boolean;
    metadata?: Record<string, unknown>;
    parsedAI?: unknown;
    filePath?: string;
  }): Chat {
    return new Chat(
      data.id,
      data.user_id,
      data.message,
      data.type,
      new Date(data.timestamp),
      data.is_read || false,
      data.metadata,
      data.parsedAI,
      data.filePath
    );
  }

  // ---- Getters ----
  get id(): string {
    return this._id;
  }

  get userId(): string {
    return this._userId;
  }

  get message(): string {
    return this._message;
  }

  get type(): "user" | "ai" {
    return this._type;
  }

  get timestamp(): Date {
    return this._timestamp;
  }

  get isRead(): boolean {
    return this._isRead;
  }

  get metadata(): Record<string, unknown> | undefined {
    return this._metadata;
  }

  get parsedAI(): unknown | undefined {
    return this._parsedAI;
  }

  get filePath(): string | undefined {
    return this._filePath;
  }

  // ---- Domain Logic ----
  isFromUser(): boolean {
    return this._type === "user";
  }

  isFromAI(): boolean {
    return this._type === "ai";
  }

  hasImage(): boolean {
    return !!this._filePath && this._filePath.trim() !== "";
  }

  markAsRead(): Chat {
    return new Chat(
      this._id,
      this._userId,
      this._message,
      this._type,
      this._timestamp,
      true,
      this._metadata,
      this._parsedAI,
      this._filePath
    );
  }

  isRecent(minutes: number = 5): boolean {
    const now = new Date();
    const diffMs = now.getTime() - this._timestamp.getTime();
    const diffMinutes = diffMs / (1000 * 60);
    return diffMinutes <= minutes;
  }

  toJSON() {
    return {
      id: this._id,
      user_id: this._userId,
      message: this._message,
      type: this._type,
      timestamp: this._timestamp.toISOString(),
      is_read: this._isRead,
      metadata: this._metadata,
      parsedAI: this._parsedAI,
      filePath: this._filePath,
    };
  }
}
